
import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Authentication } from "./Authentication";
import { SiteSelection } from "./SiteSelection";
import { Loading } from "./Loading";
import { StageSelection } from "./StageSelection";
import { Report } from "./Report";
import { ScrunchWordmark } from "@/components/logo/Wordmark";
import { ProductPreview } from "@/components/onboarding/ProductPreview";


enum Step {
  AUTHENTICATION = "authentication",
  SITE_SELECTION = "site_selection",
  STAGE_SELECTION = "stage_selection",
  LOADING = "loading",
  REPORT = "report"
}

interface Site {
  siteUrl: string;
  permissionLevel: string;
}

interface Prompt {
  prompt: string;
  type: string;
  response?: string;
  isLoading?: boolean;
}

interface OnboardingFlowProps {
  onLogin: () => void;
  isAuthenticated: boolean;
  sites: Site[];
  isLoadingSites: boolean;
  authError?: string | null;
  prompts: Prompt[];
  isGeneratingPrompts: boolean;
  onGeneratePrompts: (data: {
    siteUrl: string;
    brandName?: string;
    method: "search_console" | "manual";
  }) => void;
  onCancelGeneration: () => void;
  onTestPrompt: (prompt: Prompt) => Promise<any>;
  isTestingPrompt: boolean;
}

export function OnboardingFlow({
  onLogin,
  isAuthenticated,
  sites,
  isLoadingSites,
  authError,
  prompts,
  isGeneratingPrompts,
  onGeneratePrompts,
  onCancelGeneration,
  onTestPrompt,
  isTestingPrompt,
}: OnboardingFlowProps) {
  const [step, setStep] = useState<Step>(Step.AUTHENTICATION);
  const [onboardingState, setOnboardingState] = useState<{
    method: "search_console" | "manual";
    siteUrl: string;
    brandName: string;
    selectedStages: string[];
  }>({
    method: "search_console",
    siteUrl: "",
    brandName: "",
    selectedStages: [],
  });

  // Custom hook for minimum loading time
  const useMinimumLoadingTime = (isActive: boolean, duration: number) => {
    const [elapsed, setElapsed] = useState(false);

    useEffect(() => {
      if (isActive) {
        setElapsed(false);
        const timer = setTimeout(() => setElapsed(true), duration);
        return () => clearTimeout(timer);
      } else {
        setElapsed(false);
      }
    }, [isActive, duration]);

    return elapsed;
  };

  const MIN_LOADING_TIME = 2500;
  const minTimeElapsed = useMinimumLoadingTime(step === Step.LOADING, MIN_LOADING_TIME);

  // Ref for domain input in manual mode
  const domainInputRef = useRef<HTMLInputElement>(null);

  // Auto-advance to Step 2 when authentication completes (for Search Console flow)
  useEffect(() => {
    if (isAuthenticated && step === Step.AUTHENTICATION) {
      setOnboardingState((prev) => ({ ...prev, method: "search_console" }));
      setStep(Step.SITE_SELECTION);
    }
  }, [isAuthenticated, step]);

  // Log generation status
  useEffect(() => {
    if (isGeneratingPrompts) {
      console.log("Generation in progress...");
    }
  }, [isGeneratingPrompts]);

  // Auto-advance from step 4 (loading) to step 5 (results) when both conditions are met
  useEffect(() => {
    // Wait for generation to complete (!isGeneratingPrompts) before showing results
    // This ensures we don't show just the single generic prompt while others are still loading
    if (step === Step.LOADING && minTimeElapsed && prompts.length > 0 && !isGeneratingPrompts) {
      setStep(Step.REPORT);
    }
  }, [step, minTimeElapsed, prompts.length, isGeneratingPrompts]);


  const handleStep1Next = (stepData: {
    method: "search_console" | "manual";
    domain?: string;
    brandName?: string;
  }) => {
    setOnboardingState((prev) => ({
      ...prev,
      method: stepData.method,
      siteUrl: stepData.domain || prev.siteUrl,
      brandName: stepData.brandName || prev.brandName,
    }));
    setStep(Step.SITE_SELECTION);
  };

  const handleStep2Next = (stepData: { siteUrl: string; brandName?: string }) => {
    const finalBrandName = stepData.brandName || onboardingState.brandName;
    
    setOnboardingState((prev) => ({
      ...prev,
      siteUrl: stepData.siteUrl,
      brandName: finalBrandName,
    }));

    // Start generation in background
    const isManualDomain = onboardingState.method === "manual" || !sites.some(site => site.siteUrl === stepData.siteUrl);
    
    onGeneratePrompts({
      siteUrl: stepData.siteUrl,
      brandName: finalBrandName,
      method: isManualDomain ? "manual" : "search_console",
    });

    setStep(Step.STAGE_SELECTION);
  };

  const handleStep3Next = (selectedStages: string[]) => {
    setOnboardingState((prev) => ({ ...prev, selectedStages }));
    setStep(Step.LOADING);
  };

  const handleStep3Skip = () => {
    setStep(Step.LOADING);
  };

  const handleStep2Change = useCallback(
    (data: { siteUrl: string; brandName?: string }) => {
      setOnboardingState((prev) => ({
        ...prev,
        siteUrl: data.siteUrl,
        brandName: data.brandName || prev.brandName,
      }));
    },
    []
  );

  // Track mobile state for responsive animations
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="min-h-screen w-full bg-slate-50 flex flex-col items-center justify-center p-4">
      {/* Logo at the top */}
      <div className="mb-8">
        <a href="https://www.scrunch.com" target="_blank" rel="noopener noreferrer">
          <ScrunchWordmark className="text-slate-900" />
        </a>
      </div>

      {/* Main Card - Responsive */}
      <div 
        className="flex flex-col lg:flex-row w-full max-w-[1130px] min-h-[calc(100vh-145px)] lg:min-h-0 lg:h-[800px] overflow-hidden rounded-2xl border border-slate-200 shadow-sm bg-white relative"
        style={{
          backgroundImage: `url(${import.meta.env.BASE_URL}images/bg.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Steps 1-3 Container - Animates in on mount, out on step 4+ */}
        <AnimatePresence mode="wait">
          {(step === Step.AUTHENTICATION || step === Step.SITE_SELECTION || step === Step.STAGE_SELECTION) && (
            <motion.div
              key="steps-container"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="bg-white/90 lg:bg-white border-r-0 lg:border-r flex w-full lg:w-1/2 flex-col justify-center p-6 lg:p-16 relative z-30 h-full"
            >
              <div className="mx-auto w-full max-w-md">
                {/* Step content fades in/out */}
                <AnimatePresence mode="wait">
                  {step === Step.AUTHENTICATION && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Authentication onNext={handleStep1Next} onLogin={onLogin} />
                    </motion.div>
                  )}
                  {step === Step.SITE_SELECTION && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <SiteSelection
                        sites={sites}
                        isLoading={isLoadingSites}
                        authError={authError}
                        onNext={handleStep2Next}
                        onChange={handleStep2Change}
                        selectedSite={onboardingState.siteUrl}
                        method={onboardingState.method || "search_console"}
                        domainInputRef={domainInputRef}
                        onBack={() => setStep(Step.AUTHENTICATION)}
                        initialDomain={onboardingState.siteUrl}
                        initialBrandName={onboardingState.brandName}
                      />
                    </motion.div>
                  )}
                  {step === Step.STAGE_SELECTION && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <StageSelection 
                        onNext={handleStep3Next} 
                        onSkip={handleStep3Skip}
                        onBack={() => {
                          onCancelGeneration();
                          setStep(Step.SITE_SELECTION);
                        }}
                        selectedTopics={onboardingState.selectedStages || []}
                        onChange={(stages) => setOnboardingState(prev => ({ ...prev, selectedStages: stages }))}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 4 - Loading (always shows) */}
        {step === Step.LOADING && (
          <div className="absolute inset-0 flex items-start justify-center z-30 pointer-events-none">
            <div className="relative flex flex-col items-center justify-start w-full lg:h-full">
              {/* MiniLoading at top */}
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="z-30 relative pointer-events-auto w-full"
              >
                <Loading key="step4-loading" />
              </motion.div>
            </div>
          </div>
        )}

        {/* Step 5 - Full width for results */}
        <AnimatePresence mode="wait">
          {step === Step.REPORT && (
            <motion.div
              key="step5-results"
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="flex w-full flex-col justify-center p-4 lg:pl-16 lg:pr-16 lg:pt-16 lg:pb-0 relative z-10 min-h-screen lg:min-h-0 lg:h-full bg-white lg:bg-transparent"
            >
              <div className="mx-auto w-full max-w-full h-full">
                <Report
                  prompts={prompts}
                  onTestPrompt={onTestPrompt}
                  isTesting={isTestingPrompt}
                  selectedTopics={onboardingState.selectedStages || []}
                  siteUrl={onboardingState.siteUrl}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Promp Generator Description Card
        <AnimatePresence>
          {step === Step.AUTHENTICATION && (
            <motion.div
              key="right-panel"
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="hidden w-1/2 lg:flex items-center justify-center relative overflow-hidden border-l border-slate-100"
            >
              <div className="relative z-10 p-8 bg-white border rounded-lg w-[400px]">
                <h2 className="text-xl text-center font-bold text-slate-900 mb-4">
                  Prompt Generator
                </h2>
                <p className="text-slate-500 max-w-md mx-auto text-sm">
                  Connect Search Console or enter a domain to generate custom prompts showing how your site appears across ChatGPT, Perplexity, Grok, and other LLMs.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        */}
        {/* Single ProductPreview Instance - Visible on mobile ONLY during loading */}
        <motion.div
          className="absolute z-20 overflow-hidden bg-white shadow-lg border"
          initial={Step.AUTHENTICATION}
          animate={step}
          variants={{
            [Step.AUTHENTICATION]: isMobile ? {
              opacity: 0,
              x: 0,
              y: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none"
            } : { 
              x: 1200, 
              y: 160, 
              width: 800, 
              height: 640,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 0,
              clipPath: "inset(0 0 0 0px)",
              opacity: 1
            },
            [Step.SITE_SELECTION]: isMobile ? {
              opacity: 0,
              x: 0,
              y: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none"
            } : { 
              x: 622, 
              y: 160, 
              width: 800, 
              height: 640,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 0,
              clipPath: "inset(0 0 0 0px)",
              opacity: 1,
              transition: { duration: 0.6, ease: "easeOut" }
            },
            [Step.STAGE_SELECTION]: isMobile ? {
              opacity: 0,
              x: 0,
              y: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none"
            } : { 
              x: 306, 
              y: 260, 
              width: 823, 
              height: 640,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 0,
              clipPath: "inset(0 0 0 0px)",
              opacity: 1,
              transition: { duration: 0.6, ease: "easeOut" }
            },
            [Step.LOADING]: isMobile ? {
              opacity: 1,
              x: 0,
              y: 260,
              width: "100%",
              height: "calc(100% - 260px)",
              borderRadius: 16,
              clipPath: "inset(0 0 0 0px)",
              pointerEvents: "none", // Background only
              transition: { duration: 0.6, ease: "easeOut" }
            } : { 
              x: 70, 
              y: 260, 
              width: 986, 
              height: 640,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              clipPath: "inset(0 0 0 0px)",
              opacity: 1,
              transition: { 
                duration: 0.6, 
                ease: "easeOut"
              }
            },
            [Step.REPORT]: isMobile ? {
              opacity: 0,
              x: 0,
              y: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              transition: { duration: 0.5, ease: "easeOut" }
            } : { 
              x: 70, 
              y: 260, 
              width: 986, 
              height: 640,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              clipPath: "inset(0 0 0 0px)",
              opacity: 0,
              pointerEvents: "none",
              transition: { 
                duration: 0.5,
                ease: "easeOut"
              }
            }
          }}
        >
          <ProductPreview 
            brandName={onboardingState.brandName} 
            domain={onboardingState.siteUrl}
            selectedTopics={onboardingState.selectedStages}
            showTopicsHeader={step === Step.STAGE_SELECTION || step === Step.LOADING || step === Step.REPORT}
            hideSidebar={step === Step.LOADING || step === Step.REPORT}
            sites={sites}
            onSiteChange={(siteUrl) => {
              setOnboardingState(prev => ({ ...prev, siteUrl, brandName: "" }));
            }}
            onAddDomain={() => {
              domainInputRef.current?.focus();
            }}
            className="w-full h-full border-none shadow-none rounded-none"
          />
        </motion.div>
      </div>
      
      <div className="mt-8 text-center text-xs text-slate-400">
        © 2025 Scrunch. All rights reserved.
      </div>
    </div>
  );
}
